// Global Instructions Rule Applied!
//
// AgentMarkdown — the ONE markdown renderer in the app, for agent-written
// DOCUMENTS (pipeline-meeting minutes / prep / themes with
// content_format='markdown'). Approved deviation, owner 2026-09-02 — see
// docs/FRONTEND_DESIGN_PATTERNS.md "Agent long-form markdown".
//
// Constrained on purpose: the prompts ask for '##'/'###' headings, bullets,
// bold, one pipe table and short block-quotes, and this renderer accepts
// exactly that. Raw HTML is skipped, images/links/code are unwrapped to their
// text, so nothing a model emits can inject markup or fetch a remote asset.
// Typography matches the plain-text agent treatment (13px body) with the
// app's small-caps grey section labels for headings and the signal-card's
// serif-italic evidence style for quotes.
import React from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

const ALLOWED_ELEMENTS = [
  'p', 'h2', 'h3', 'h4', 'ul', 'ol', 'li', 'strong', 'em', 'br',
  'blockquote', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'del'
]

interface AgentMarkdownProps {
  source: string
  className?: string
}

export const AgentMarkdown: React.FC<AgentMarkdownProps> = ({ source, className = '' }) => (
  <div data-component='AgentMarkdown' className={`text-[13px] text-gray-800 leading-relaxed ${className}`}>
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      allowedElements={ALLOWED_ELEMENTS}
      unwrapDisallowed
      skipHtml
      components={{
        // '#' is not part of the format contract; if a model emits one it
        // renders as a section label rather than a page title.
        h1: ({ children }) => (
          <div className='text-[11.5px] font-semibold uppercase tracking-wide text-gray-500 mt-3 mb-1 first:mt-0'>
            {children}
          </div>
        ),
        h2: ({ children }) => (
          <div className='text-[11.5px] font-semibold uppercase tracking-wide text-gray-500 mt-3 mb-1 first:mt-0'>
            {children}
          </div>
        ),
        h3: ({ children }) => (
          <div className='text-[12.5px] font-semibold text-gray-800 mt-2 mb-0.5'>{children}</div>
        ),
        h4: ({ children }) => (
          <div className='text-[12.5px] font-semibold text-gray-700 mt-1.5 mb-0.5'>{children}</div>
        ),
        p: ({ children }) => <p className='my-1'>{children}</p>,
        ul: ({ children }) => <ul className='list-disc pl-5 my-1 space-y-0.5'>{children}</ul>,
        ol: ({ children }) => <ol className='list-decimal pl-5 my-1 space-y-0.5'>{children}</ol>,
        li: ({ children }) => <li className='pl-0.5'>{children}</li>,
        strong: ({ children }) => <span className='font-semibold'>{children}</span>,
        blockquote: ({ children }) => (
          <blockquote className='border-l-2 border-gray-300 pl-2.5 my-1.5 text-gray-600 italic font-serif'>
            {children}
          </blockquote>
        ),
        table: ({ children }) => (
          <div className='overflow-x-auto my-1.5'>
            <table className='text-[12.5px] border-collapse w-auto'>{children}</table>
          </div>
        ),
        th: ({ children }) => (
          <th className='border border-gray-200 bg-gray-50 px-2 py-0.5 text-left font-semibold whitespace-nowrap'>
            {children}
          </th>
        ),
        td: ({ children }) => (
          <td className='border border-gray-200 px-2 py-0.5 align-top'>{children}</td>
        )
      }}
    >
      {source}
    </ReactMarkdown>
  </div>
)
